import { factor } from '@fameacc/money';
import { z } from 'zod';
import { auswertungHuelleGestalt } from './auswertungen.js';
import { voranmeldungSchema } from './einstellungen.js';
import { centAmount, isoDate, uuid } from './posting.js';
// --- Umsatzsteuer-Voranmeldung: das Kennzahlblatt [UV-api] ------------------
//
// `GET /api/v1/auswertungen/ustva` und `GET /api/v1/auswertungen/ustva/nachweis`
// — Spec `docs/superpowers/specs/2026-10-09-ustva-kennzahlblatt-design.md`,
// Abschnitte 2.4, 2.8, 4.2 und 5, Plan
// `docs/superpowers/plans/2026-10-09-ustva-kennzahlblatt-umsetzung.md` Task 2
// `[UV-api]`. Entscheidungen `D-1`, `D-2`, `D-3`, `D-5`, `D-10`, `D-13`,
// `D-14`, `D-20`, `D-21`, `D-23`, `D-25`, `D-26` bis `D-29`, `D-32`, `D-35`,
// `D-36`, `D-37`, `D-41`, `D-44`, `D-45`.
//
// **Ein Kennzahlblatt zum Übertragen, keine Übermittlung** (`D-1`): Der
// Anwender trägt die Zahlen von Hand in „Mein ELSTER" ein. Gerechnet wird in
// SQL aus EINER Funktion (`ustva_einzelposten()`, Migration `0292`); das Blatt
// summiert ihre Posten, der Nachweis listet sie (`D-29`, `E-1813`). Die
// Euro-Abrundung der Bemessungsgrundlagen geschieht genau einmal, über
// `floorToEuros()` aus `@fameacc/money` (`D-10`).
//
// **Die Sätze stehen hier, im Vertrag** (Auswertungen `D-9`, `D-3`): Was die
// Antwort über sich selbst sagt — „nicht anwendbar", „Vorschau", die Gründe
// für „nicht vollständig" —, steht als Satz in der Antwort und nicht im JSX,
// und die festen Sätze stehen genau einmal, in {@link USTVA_SATZ}.
/** Die zwei Zeitraumarten, für die ein Blatt gerechnet wird (§ 18 Abs. 1 und 2 UStG). */
export const ustvaZeitraumartSchema = z.enum(['monat', 'quartal']);
/**
 * Die Art eines Einzelpostens — wie `ustva_einzelposten()` sie vergibt
 * (Kopfkommentar von `0292_ustva_rechnung.sql`, Abschnitt „Die Arten").
 *
 * **Ein Wert, den die Funktion liefert und dieser Vertrag nicht kennt, wirft
 * im Dienst mit Namen** (`ustva.query.ts`), statt als unbekannte Zeile
 * durchzurutschen — dieselbe Haltung wie `alsSteuerart()` der Steuerseite.
 */
export const ustvaPostenArtSchema = z.enum([
    'vereinnahmung',
    'kontierung_umsatz',
    'leistung',
    'leistung_korrektur',
    'vorsteuer_journal',
    'vorsteuer_eingangsrechnung',
    'vorsteuer_uebergang',
    'rc_bemessung',
    'rc_steuer',
    'rc_vorsteuer',
    'nicht_in_meldung',
    'befund_steuergruppe_ohne_kennzahl',
    'befund_automatik_ohne_schluessel',
    'auskunft_gutschrift_storno',
    'auskunft_kontierung_steuer_abweichend',
    'auskunft_moegliche_doppelzaehlung',
]);
/** Die Markierungen eines Einzelpostens (Kopfkommentar von `0292`). */
export const ustvaMarkierungSchema = z.enum([
    'altforderung',
    'uebergang_altforderung',
    'uebergang_gebucht',
    'steuer_verdaechtig',
    'verspaetet_erfasst',
    'kontierung_mit_vorsteuer',
    'kontierung_storniert',
    'uebergangsquelle',
    'nicht_nachbuchbar',
    'rechnung_nicht_gezaehlt',
    'kleinunternehmer',
]);
/**
 * Die Abschnitte des Nachweises, die KEINE Kennzahl sind: die Posten ohne
 * Kennzahl je Art (Auskunft und Befund, „Nicht in dieser Meldung") und die
 * Posten, die an einem Tag unter Kleinunternehmer liegen und deshalb in keine
 * Kennzahl eingehen (Spec 4.2: „ein Kleinunternehmerbeleg trägt keine
 * Kennzahl"; § 13b unter Kleinunternehmer, `D-36`).
 */
export const ustvaNachweisAbschnittSchema = z.enum([
    'nicht_in_meldung',
    'befund_steuergruppe_ohne_kennzahl',
    'befund_automatik_ohne_schluessel',
    'auskunft_gutschrift_storno',
    'auskunft_kontierung_steuer_abweichend',
    'auskunft_moegliche_doppelzaehlung',
    'kleinunternehmer',
]);
/** Die Art einer Zeile des Vordrucks (`ustva_zeile.art`, Migration `0291`). */
export const ustvaZeilenArtSchema = z.enum(['bemessung', 'steuer', 'vorsteuer', 'ergebnis']);
/** Ein Steuersatz als `Factor` (`0.19`), nie als `number`. */
const satzFaktor = z.string().transform((wert, ctx) => {
    try {
        return factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Steuersatz' });
        return z.NEVER;
    }
});
const jahrFeld = z.coerce.number().int().min(2000).max(9999);
const quartalFeld = z.coerce.number().int().min(1).max(4);
const monatFeld = z.coerce.number().int().min(1).max(12);
/**
 * Prüft die Zeitraumangabe einer Anfrage: entweder gar keine (die Vorgabe des
 * Dienstes), oder `jahr` und **genau eines** von `quartal` und `monat`.
 */
function zeitraumangabePruefen(wert, ctx, pflicht) {
    const leer = wert.jahr === undefined && wert.quartal === undefined && wert.monat === undefined;
    if (leer && !pflicht)
        return;
    if (wert.jahr === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['jahr'], message: 'jahr fehlt' });
    }
    if ((wert.quartal === undefined) === (wert.monat === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quartal'],
            message: 'genau eines von quartal und monat angeben',
        });
    }
}
/**
 * Die Abfrageparameter von `GET /api/v1/auswertungen/ustva`.
 *
 * **Ohne Angabe** gilt der jüngste abgeschlossene Zeitraum in der
 * eingestellten Zeitraumart, sonst der laufende (Spec 5.2). **Mit Angabe**
 * `jahr` und genau eines von `quartal` oder `monat` — ein Monat aus einem
 * Quartalszeitraum ist eine legitime Frage (Spec 4.2, `E-1810`), das Blatt
 * rechnet und sagt es.
 *
 * **Exportiert und deshalb in `openapi.json`** (Handbuch §3.760, `E-1816`): Ein
 * Abfrageschema, das nur im Controller stünde, liesse die Parameter still aus
 * der Schnittstellenbeschreibung fallen. Die Regel „genau eines" ist ein
 * `superRefine()` und lässt sich in JSON-Schema nicht ausdrücken; die
 * Beschreibung der Route nennt sie.
 */
export const ustvaAnfrageSchema = z
    .object({
    jahr: jahrFeld.optional(),
    quartal: quartalFeld.optional(),
    monat: monatFeld.optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    zeitraumangabePruefen(wert, ctx, false);
});
/** Die Seitengrösse des Nachweises ohne Angabe. */
export const USTVA_NACHWEIS_GROESSE_VORGABE = 100;
/**
 * Die Abfrageparameter von `GET /api/v1/auswertungen/ustva/nachweis`.
 *
 * Zeitraum wie beim Blatt, hier aber **Pflicht**; dazu **genau eines** von
 * `kennzahl` (zweistellig, etwa `81`) und `abschnitt` (die Posten ohne
 * Kennzahl, {@link ustvaNachweisAbschnittSchema}). `seite` beginnt bei 1,
 * `groesse` höchstens 500, ohne Angabe {@link USTVA_NACHWEIS_GROESSE_VORGABE}.
 */
export const ustvaNachweisAnfrageSchema = z
    .object({
    jahr: jahrFeld.optional(),
    quartal: quartalFeld.optional(),
    monat: monatFeld.optional(),
    kennzahl: z
        .string()
        .regex(/^\d{2}$/, 'Kennzahl zweistellig erwartet')
        .optional(),
    abschnitt: ustvaNachweisAbschnittSchema.optional(),
    seite: z.coerce.number().int().min(1).optional(),
    groesse: z.coerce.number().int().min(1).max(500).optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    zeitraumangabePruefen(wert, ctx, true);
    if ((wert.kennzahl === undefined) === (wert.abschnitt === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['kennzahl'],
            message: 'genau eines von kennzahl und abschnitt angeben',
        });
    }
});
/**
 * Ein Voranmeldungszeitraum: Art, Jahr, Quartal ODER Monat, die Kennung
 * (`2026-Q4`, `2026-10` — dieselbe, die der Protokolleintrag des PDF trägt,
 * Spec 5.6), die Bezeichnung für den Leser und die Datumsspanne.
 */
export const ustvaZeitraumSchema = z
    .object({
    art: ustvaZeitraumartSchema,
    jahr: z.number().int(),
    quartal: z.number().int().min(1).max(4).nullable(),
    monat: z.number().int().min(1).max(12).nullable(),
    kennung: z.string().min(1),
    bezeichnung: z.string().min(1),
    von: isoDate,
    bis: isoDate,
})
    .strict();
/** Ein wählbarer Zeitraum der Zeitraumwahl (Spec 5.2), mit dem Abschlussstand. */
export const ustvaWaehlbarerZeitraumSchema = z
    .object({
    ...ustvaZeitraumSchema.shape,
    /** Jeder Monat des Zeitraums steht in `period` auf `closed`. */
    abgeschlossen: z.boolean(),
})
    .strict();
/**
 * Eine Unterzeile „davon vor dem Meldewechsel ausgestellt" je Klasse
 * (Spec 3.5, `D-24`, `D-44`): Altforderungen ohne gebuchte Steuer, und die
 * gebuchte Klasse samt Live-Push.
 */
export const ustvaUnterzeileSchema = z
    .object({
    markierung: z.enum(['uebergang_altforderung', 'uebergang_gebucht']),
    bezeichnung: z.string().min(1),
    betrag: centAmount,
    posten: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Steuer zu einer Bemessungsgrundlage, wie der Vordruck sie rechnet
 * (Spec 2.4, 2.8): volle-Euro-Basis mal Satz am Zeitraumende, auf den Cent.
 * Nur für die Kennzahlen, deren Satz aus dem Steuerschlüssel kommt (81, 86).
 */
export const ustvaSteuerNachVordruckSchema = z
    .object({
    satz: satzFaktor,
    steuer: centAmount,
})
    .strict();
/**
 * Eine Kennzahlzeile des Blattes.
 *
 * **`betragVorRundung` ist die Summe der Einzelposten in Cent, `betrag` der
 * Wert für den Vordruck** (`E-1813`): bei einer Bemessungsgrundlage auf volle
 * Euro abgerundet (`D-10`), sonst gleich. Kennzahl 83 hat keine Posten; dort
 * sind beide Felder das Ergebnis der Rechnung aus Spec 2.8.
 *
 * `amtlichGeprueft = false` heisst: Zuordnung nicht gegen den amtlichen
 * Vordruck 2026 geprüft (Spalte aus `0291`, Tor `G-uvvordruck`).
 */
export const ustvaKennzahlzeileSchema = z
    .object({
    kennzahl: z.string().regex(/^\d{2}$/),
    zeile: z.number().int().nullable(),
    bezeichnung: z.string().min(1),
    art: ustvaZeilenArtSchema,
    betrag: centAmount,
    betragVorRundung: centAmount,
    posten: z.number().int().nonnegative(),
    amtlichGeprueft: z.boolean(),
    fundstelle: z.string().min(1),
    steuerNachVordruck: ustvaSteuerNachVordruckSchema.nullable(),
    unterzeilen: z.array(ustvaUnterzeileSchema),
})
    .strict();
/**
 * Eine Zeile des Abschnitts „Nicht in dieser Meldung" (Spec 2.2, `D-13`,
 * `D-40`): je Zuordnung mit Regel `nicht_in_meldung` die Nettosumme und Zahl
 * der Posten des Zeitraums samt Begründung — auch mit null, damit niemand es
 * für vergessen hält.
 */
export const ustvaNichtInMeldungSchema = z
    .object({
    quelle: z.enum(['ausgang_szenario', 'eingang_regime']),
    wert: z.string().min(1),
    begruendung: z.string().min(1),
    netto: centAmount,
    posten: z.number().int().nonnegative(),
})
    .strict();
/** Eine Kennzahl ohne Quelle in diesem System, mit ihrem Satz (Spec 2.3, `D-14`). */
export const ustvaOhneQuelleSchema = z
    .object({
    kennzahl: z.string().regex(/^\d{2}$/),
    bezeichnung: z.string().min(1),
    satz: z.string().min(1),
})
    .strict();
/** Eine Bewegung auf den fälligen Umsatzsteuerkonten ausserhalb der Ist-Versteuerung (Probe B). */
export const ustvaFremdbewegungSchema = z
    .object({
    konto: z.string().min(1),
    quelle: z.string().min(1),
    betrag: centAmount,
})
    .strict();
/**
 * Der Kasten „Probe" unter der Tabelle (Spec 3.6, 5.3, `D-21`).
 *
 * - `steuerNachVordruck`: Steuer zu 81 und 86 aus der Euro-Basis.
 * - `gebuchteFaelligeSteuer`: die Umbuchungen der Ist-Versteuerung des
 *   Zeitraums (Probe A, Seite „ist").
 * - `rundungsdifferenz`: das eine minus das andere — **kein Befund**, eine
 *   benannte Zahl.
 * - `euroAbrundung`: um wie viel die Steuer nach Vordruck durch die
 *   Euro-Abrundung der Basis kleiner ist als die Steuer auf die Centbasis.
 * - `satzprobe`: Summe der Abweichungen Netto mal Satz gegen die anteilige
 *   gebuchte Steuer (benannter Posten).
 * - `befundeProbeA`: Beleg-Konto-Paare mit Abweichung in Probe A.
 * - `zuordnungMehrdeutig`: Forderungszeilen ohne Aufteilung (`E-1792`).
 * - `fremdbewegungen`: Probe B ohne die Umbuchungen.
 */
export const ustvaProbeSchema = z
    .object({
    steuerNachVordruck: centAmount,
    gebuchteFaelligeSteuer: centAmount,
    rundungsdifferenz: centAmount,
    euroAbrundung: centAmount,
    satzprobe: centAmount,
    befundeProbeA: z.number().int().nonnegative(),
    zuordnungMehrdeutig: z.number().int().nonnegative(),
    fremdbewegungen: z.array(ustvaFremdbewegungSchema),
})
    .strict();
/**
 * Die Gründe für `vollstaendig: false`, gezählt (Spec 5.4, `D-23`, `D-45`,
 * `E-1808`) — dazu der Satzwechsel im Zeitraum (Spec 2.8, `E-1797`).
 */
export const ustvaBefundeSchema = z
    .object({
    /** Belege in `ust_umbuchung_offen` mit Tilgung im Zeitraum. */
    umbuchungOffen: z.number().int().nonnegative(),
    /** Abweichungen in Probe A und Forderungszeilen `zuordnung_mehrdeutig`. */
    probe: z.number().int().nonnegative(),
    /** Kontierungspositionen auf einem Automatikkonto ohne wirksamen Schlüssel. */
    automatikOhneSchluessel: z.number().int().nonnegative(),
    /** Steuergruppen ohne Kennzahl mit Tilgung im Zeitraum. */
    steuergruppeOhneKennzahl: z.number().int().nonnegative(),
    /** Offene Klärfälle (`zahlung_klaerung`) mit Bewegung im Zeitraum. */
    klaerfaelle: z.number().int().nonnegative(),
    /** Ein Steuersatz zu 81 oder 86 hat sich im Zeitraum geändert. */
    satzwechsel: z.boolean(),
})
    .strict();
/**
 * Woher der Tag des Meldewechsels kommt (`D-48`, Migration `0303`):
 * `einstellung` — aus `steuerprofil.meldewechsel` der Fassung, die heute gilt;
 * `journalbeginn` — die Fassung trägt keinen Tag, und `ustva_meldewechsel()`
 * fällt auf `einrichtung.journalbeginn` zurück.
 */
export const ustvaMeldewechselQuelleSchema = z.enum(['einstellung', 'journalbeginn']);
/**
 * Was die Einstellung über den Zeitraum sagt (Spec 4.1, 4.2).
 *
 * `voranmeldung` ist die Angabe der **massgeblichen Fassung**: der am letzten
 * Tag des Zeitraums geltenden, und gilt dort Kleinunternehmer, der letzten
 * Fassung mit Regelbesteuerung im Zeitraum (`D-5`). `abweichend` heisst: Die
 * gewählte Zeitraumart ist nicht die eingestellte.
 *
 * **`meldewechsel` ist der Tag, ab dem dieses System die Voranmeldung trägt**
 * (`D-48`, `E-1872`) — derselbe Wert, den Probe und Einzelposten lesen:
 * `ustva_meldewechsel()` (Migration `0303`), im Dienst per SQL gelesen und nie
 * in TypeScript nachgerechnet. Er gilt, wie die Funktion, nach der Fassung von
 * **heute**, nicht nach der massgeblichen Fassung des Zeitraums. `null` nur,
 * wenn weder die Fassung einen Tag trägt noch der Beginn der Buchführung
 * gesetzt ist (eine unvollständige Einrichtung). `meldewechselQuelle` sagt,
 * ob der Tag eingestellt ist oder der Rückfall auf den Beginn der Buchführung.
 */
export const ustvaEinstellungSchema = z
    .object({
    voranmeldung: voranmeldungSchema.nullable(),
    dauerfrist: z.boolean(),
    finanzamt: z.string().nullable(),
    abweichend: z.boolean(),
    meldewechsel: isoDate.nullable(),
    meldewechselQuelle: ustvaMeldewechselQuelleSchema,
})
    .strict();
const MELDEWECHSEL_MONATE = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
];
/**
 * Die Kopfzeile des Blattes zum Meldewechsel (`E-1872`) — genau einmal
 * aufgeschrieben, von der Seite und vom PDF gelesen (Auswertungen `D-9`, `D-3`):
 * „Eigene Voranmeldung ab Oktober 2026", beim Rückfall „ab Beginn der
 * Buchführung, 01.01.2026". Ein Blatt für einen Zeitraum vor diesem Tag ist
 * damit als eines erkennbar, das dieses System nicht meldet.
 */
export function ustvaMeldewechselSatz(einstellung) {
    const tag = einstellung.meldewechsel;
    if (tag === null) {
        return 'Eigene Voranmeldung ab Beginn der Buchführung — der Beginn der Buchführung ist noch nicht gesetzt.';
    }
    if (einstellung.meldewechselQuelle === 'journalbeginn') {
        return `Eigene Voranmeldung ab Beginn der Buchführung, ${tag.slice(8, 10)}.${tag.slice(5, 7)}.${tag.slice(0, 4)}`;
    }
    const monat = MELDEWECHSEL_MONATE[Number(tag.slice(5, 7)) - 1] ?? tag.slice(5, 7);
    return `Eigene Voranmeldung ab ${monat} ${tag.slice(0, 4)}`;
}
/**
 * Die Antwort von `GET /api/v1/auswertungen/ustva` — das Kennzahlblatt.
 *
 * **Das Blatt wird nie verweigert** (Spec 5.4): Ein unvollständiges Blatt mit
 * benannter Lücke ist mehr wert als keines. `vollstaendig` und
 * `unvollstaendigSatz` sagen es über der Tabelle; `befunde` zählt die Gründe.
 *
 * **`anwendbar: false`** heisst: kein einziger Tag des Zeitraums unter
 * Regelbesteuerung, eine eingestellte Befreiung (`jahr`, `keine`) oder
 * Soll-Versteuerung — dann trägt die Antwort keine Kennzahl, aber den Satz und
 * die Hinweise (`E-1806`).
 */
export const ustvaBlattSchema = z
    .object({
    ...auswertungHuelleGestalt,
    voranmeldungszeitraum: ustvaZeitraumSchema,
    vordruckJahr: z.number().int(),
    anwendbar: z.boolean(),
    nichtAnwendbarSatz: z.string().min(1).nullable(),
    vorschau: z.boolean(),
    vollstaendig: z.boolean(),
    unvollstaendigSatz: z.string().min(1).nullable(),
    befunde: ustvaBefundeSchema,
    hinweise: z.array(z.string().min(1)),
    kennzahlen: z.array(ustvaKennzahlzeileSchema),
    nichtInDieserMeldung: z.array(ustvaNichtInMeldungSchema),
    ohneQuelle: z.array(ustvaOhneQuelleSchema),
    probe: ustvaProbeSchema.nullable(),
    einstellung: ustvaEinstellungSchema,
    zeitraeume: z.array(ustvaWaehlbarerZeitraumSchema),
    uebertragungshilfe: z.string().min(1),
})
    .strict();
/** Der Bezug eines Einzelpostens auf einen Ausgangsbeleg. */
const ustvaBelegbezugSchema = z.object({ id: uuid, nummer: z.string().nullable() }).strict();
/** Der Bezug auf eine Eingangsrechnung: Kreditor und fremde Nummer (Spec 5.3). */
const ustvaEingangsbezugSchema = z
    .object({ id: uuid, kreditor: z.string().min(1), fremdeNummer: z.string().nullable() })
    .strict();
/** Der Bezug auf eine Kontierung samt Bankbewegung. */
const ustvaKontierungsbezugSchema = z
    .object({ id: uuid, bankbewegungId: uuid.nullable() })
    .strict();
/** Der Bezug auf eine Buchung des Journals. */
const ustvaBuchungsbezugSchema = z.object({ id: uuid, nummer: z.string().min(1) }).strict();
/**
 * Ein Einzelposten des Nachweises — eine Zeile von `ustva_einzelposten()` mit
 * aufgelöstem Bezug. `betrag` ist der Beitrag zur Kennzahl in Euro und Cent;
 * `steuer` trägt nur die Auskunft zu Gutschrift/Storno und zur Abweichung der
 * Kontierungssteuer. `rechtlicherTag` ist bei einer verspätet erfassten
 * Eingangsrechnung der Tag, zu dem sie rechtlich gehörte (Spec 2.6 b).
 */
export const ustvaPostenSchema = z
    .object({
    art: ustvaPostenArtSchema,
    kennzahl: z.string().nullable(),
    datum: isoDate,
    betrag: centAmount,
    steuer: centAmount.nullable(),
    schluessel: z.string().nullable(),
    rechtlicherTag: isoDate.nullable(),
    markierungen: z.array(ustvaMarkierungSchema),
    beleg: ustvaBelegbezugSchema.nullable(),
    eingangsrechnung: ustvaEingangsbezugSchema.nullable(),
    kontierung: ustvaKontierungsbezugSchema.nullable(),
    buchung: ustvaBuchungsbezugSchema.nullable(),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/ustva/nachweis` — die
 * Einzelposten einer Kennzahl oder eines Abschnitts, seitenweise (`D-29`).
 *
 * **`summe` und `anzahl` gelten für ALLE Posten, nicht nur für die Seite.**
 * Für eine Kennzahl ist `summe` gleich `betragVorRundung` derselben Zeile im
 * Blatt (`E-1813`) — die Tabelle kann von ihrem Nachweis nicht abweichen.
 */
export const ustvaNachweisSchema = z
    .object({
    voranmeldungszeitraum: ustvaZeitraumSchema,
    kennzahl: z.string().nullable(),
    abschnitt: ustvaNachweisAbschnittSchema.nullable(),
    summe: centAmount,
    anzahl: z.number().int().nonnegative(),
    seite: z.number().int().min(1),
    groesse: z.number().int().min(1),
    posten: z.array(ustvaPostenSchema),
})
    .strict();
/**
 * Die festen Sätze des Blattes — genau einmal aufgeschrieben (`D-3`, Spec 5.2,
 * 5.4, 5.5, 4.3, 6.1 Nr. 12). Sätze mit einer Zahl darin baut der Dienst.
 */
export const USTVA_SATZ = {
    nichtAnwendbarKleinunternehmer: 'Als Kleinunternehmer (§ 19 UStG) geben Sie keine Umsatzsteuer-Voranmeldung ab.',
    keineVoranmeldungspflicht: 'Keine Voranmeldungspflicht eingestellt: Für diesen Zeitraum ist eine jährliche Abgabe oder keine Abgabe hinterlegt. Das Finanzamt kann von der Voranmeldung befreien (§ 18 Abs. 2 Satz 3 UStG); dann werden keine Kennzahlen gerechnet, die niemand meldet.',
    sollVersteuerung: 'Versteuerung nach vereinbarten Entgelten wird nicht unterstützt. Dieses Blatt rechnet nur nach vereinnahmten Entgelten (§ 20 UStG).',
    vorschau: 'Vorschau — der Zeitraum ist nicht abgeschlossen; die Zahlen können sich bis zum Abschluss ändern.',
    uebertragungshilfe: 'Dieses Blatt ist eine Übertragungshilfe. Die Voranmeldung geben Sie in Mein ELSTER ab; dieses System übermittelt nichts.',
    zeitraumNichtEingestellt: 'Voranmeldungszeitraum nicht eingestellt — beide Zeitraumarten stehen zur Wahl. Die Einstellung steht unter Einstellungen, Steuer.',
    zusammenfassendeMeldung: 'Kennzahl 21 ist nicht null: Für diese Leistungen ist eine Zusammenfassende Meldung erforderlich (§ 18a UStG). Sie ist in diesem System nicht gebaut.',
    ossNichtEnthalten: 'Umsätze im OSS-Verfahren sind nicht enthalten; sie werden im besonderen Besteuerungsverfahren gemeldet (§ 18j UStG), nicht in dieser Voranmeldung.',
    dauerfrist: 'Dauerfristverlängerung eingestellt: Die Abgabefrist verlängert sich um einen Monat (§§ 46 bis 48 UStDV). Sie wird auf diesem Blatt genannt, nicht gerechnet.',
    sondervorauszahlung: 'Sondervorauszahlung anrechnen: nicht gerechnet. Bei monatlicher Abgabe mit Dauerfristverlängerung ist sie in der Dezember-Voranmeldung anzurechnen (§ 47 UStDV).',
    satzwechsel: 'Ein Steuersatz zu Kennzahl 81 oder 86 hat sich im Zeitraum geändert. Der Vordruck verlangt dafür eine eigene Zeile; das Blatt rechnet mit dem Satz am Zeitraumende und mischt nicht still.',
    amtlichNichtGeprueft: 'Kennzahlen ohne den Vermerk „amtlich geprüft" sind nicht gegen den amtlichen Vordruck 2026 geprüft; ihre Zuordnung ist eine Festlegung, die der Steuerberater bestätigen muss.',
};
//# sourceMappingURL=ustva.js.map